import { useEffect, useRef } from 'react';
import { isEmpty } from 'lodash';
import { useAuth } from 'react-oidc-context';
import OneSignalReact from 'react-onesignal'; // Ensure proper import if needed
import { userManager } from 'services/user-manager';
import { markLogout, isLoggedOutByOtherTab } from 'utils/url';
// import { useData } from '../components/Common/LoginMenu/hooks';

const getUserName = (profile) =>
  profile &&
  (profile.given_name ||
    profile.name ||
    profile.email ||
    profile.preferred_username);
const getAvatarName = (authUser) => {
  if (authUser) {
    const { firstName, lastName, email } = authUser;

    if (isEmpty(firstName) && isEmpty(lastName))
      return String(email).substring(0, 2).toUpperCase();
    else
      return (
        String(firstName).charAt(0).toUpperCase() +
        String(lastName).charAt(0).toUpperCase()
      );
  }
};

const getDisplayName = (profile) => {
  if (profile) {
    const { given_name, family_name, name } = profile;

    if (isEmpty(given_name) && isEmpty(family_name)) return name;
    else return given_name + ' ' + family_name;
  }
};

const getFormattedEmail = (email) => email.substring(0, email.lastIndexOf('@'));

const getFormattedPhone = (phone) =>
  phone.substring(phone.length - 4, phone.length);

const persistStorage = (data) => {
  localStorage.setItem('persist-data', JSON.stringify(data));
};

export function useProfile() {
  const auth = useAuth();
  const oidcUser = auth.user;
  const { access_token: accessToken, profile } = oidcUser || {};

  // const authuserrr = useData();
  const isLoggedIn = auth.isAuthenticated;

  // login/logout keep the previous signatures (callable with no args).
  const login = () => auth.signinRedirect();
  const logout = () => auth.signoutRedirect();

  // When this tab's session began. Used to ignore logout stamps that are older than
  // our own session (e.g. we logged out yesterday, came back today -> must NOT log out).
  const sessionStartedAt = useRef(null);
  useEffect(() => {
    if (oidcUser && !sessionStartedAt.current) sessionStartedAt.current = Date.now();
    if (!oidcUser) sessionStartedAt.current = null;
  }, [oidcUser]);

  // Another tab logged out: the token is in sessionStorage (per-tab), so this tab
  // still holds a valid user. The other tab wrote a timestamp into localStorage and
  // the `storage` event brought it here, so drop our user and reload.
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== 'logoutAt') return;

      // Only react to a logout that happened after this tab logged in, and only once.
      if (!isLoggedOutByOtherTab(sessionStartedAt.current)) return;

      sessionStartedAt.current = null; // guard: never handle the same stamp twice
      userManager.removeUser();
      window.location.reload();
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  if (profile && profile.email)
    profile.email = getFormattedEmail(profile.email);
  if (profile && profile.phone)
    profile.phone = getFormattedPhone(profile.phone);

  const userName = getUserName(profile);

  const avatarName = getAvatarName("authUser");

  const displayName = getDisplayName(profile);

  // Non-async. Fire-and-forget OneSignal; always call logout().
  const handleLogout = () => {
    try {
      if (OneSignalReact) {
        // Kick off both ops in the background. Never block on them.
        Promise.allSettled([
          OneSignalReact.logoutEmail?.(),
          OneSignalReact.removeExternalUserId?.()
        ]).catch(() => {
          // Shouldn't happen because allSettled never rejects, but just in case.
        });
      }
    } catch (err) {
      // Catch any sync errors (e.g., bad reference)
      console.error("OneSignal sync error:", err);
    } finally {
      // Tell the other tabs we logged out (they compare this stamp with their own
      // session start time), then log out the app user immediately.
      markLogout();
      logout();
    }
  };

  return {
    accessToken,
    userName,
    avatarName,
    displayName,
    profile,
    login,
    logout: handleLogout,
    isLoggedIn,
    isLoading: auth.isLoading,
    persistStorage,
    oidcUser
    //authUser,
  };
}
